import AddCircleIcon from "@mui/icons-material/AddCircle";
import { GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { caseRepository } from "../../../../services/caseRepository";
import useGet from "../../../../services/hooks/useGet";
import usePost from "../../../../services/hooks/usePost";
import { apiEndpoints } from "../../../../shared/constants/api-constant";
import { Button, Heading } from "../../../../shared/ReusableComponents";
import { GridTable } from "../../../../shared/ReusableComponents/GridTable";
import IconButton from "../../../../shared/ReusableComponents/IconButton";
import { CommonFunctions } from "../../../../shared/utils/CommonFunctions";
import { DateFormatter } from "../../../../shared/utils/DateFormatter";
import TelephoneOrdersDialog from "./TelephoneOrdersDialog";
import AdministerDialog from "./AdministerDialog";
import { Box } from "@mui/material";
import { AppConfigurationHelper } from "../../../../services/AppConfigurationHelper";

export interface ITelephoneOrdersData {
  startDate: string;
  medicineName: string;
  route: string;
  dose: string;
  initials: string;
  counterSignedBy: string;
  prescribedBy: string;
  isTeleOrder: boolean;
}

function TelephoneOrders({ caseId }: { caseId: number }) {
  const columns: GridColDef[] = [
    {
      field: "startDate",
      headerName: "Date, Time",
      width: 160,
      sortable: true,
      valueGetter: (value, row) =>
        `${DateFormatter.formatDate(value)}, ${DateFormatter.formatTime(
          row.startDate
        )}`,
    },
    {
      field: "medicineName",
      headerName: "Medicine",
      sortable: true,
      width: 120,
      valueGetter: (value, row) => row.medicineName,
    },
    {
      field: "route",
      headerName: "Route",
      valueGetter: (value, row) => row.route,
    },
    {
      field: "dose",
      headerName: "Dose",
      valueGetter: (value, row) => row.dose,
    },
    {
      field: "initials",
      headerName: "Check Initials (N1)",
    },
    {
      field: "counterSignedBy",
      headerName: "Counter Signed By",
      valueGetter: (value, row) => {
        const name = row.counterSignedBy;
        const displayName = CommonFunctions.getDisplayValue(name);
        return displayName ===
          AppConfigurationHelper.Instance().displayDefaults?.emptyFallback
          ? displayName
          : CommonFunctions.formatName(displayName, { suffixKey: "rn" });
      },
    },
    {
      field: "prescribedBy",
      headerName: "Prescribed By",
      valueGetter: (value, row) => {
        const name = row.prescribedby;
        const displayName = CommonFunctions.getDisplayValue(name);
        return displayName ===
          AppConfigurationHelper.Instance().displayDefaults?.emptyFallback
          ? displayName
          : CommonFunctions.formatName(row.prescribedby, { prefixKey: "doctor" });
      },
    },
    {
      field: "isAdminister",
      headerName: "Action",
      width: 120,
      renderCell: (params) => {
        const { administerOn, dose } = params.row;
        const dateTime = `${DateFormatter.formatDate(
          administerOn
        )}, ${DateFormatter.formatTime(administerOn)}`;
        const message = `${dateTime} ${dose} Administered by ${params.row.initials}`;
        return params.row.isAdminister ? (
          <span title={message}>{message}</span>
        ) : (
          <Box sx={{ ...commonCellStyle }}>
            <Button
              id="administer"
              onClick={() => openAdministerDialog(params.row)}
              text="Administer"
              className="tinyBtn"
              style={{
                color: "var(--color-white)",
                textTransform: "capitalize",
                width: "90px",
                p: "0px 40px",
              }}
            />
          </Box>
        );
      },
    },
  ];

  const [open, setOpen] = React.useState(false);
  const [isAdminOpen, setIsAdminOpen] = React.useState(false);
  const [adminRowData, setAdminRowData] = React.useState({});
  const [originalData, setOriginalData] = React.useState<
    ITelephoneOrdersData[]
  >([]);

  const [loading, setLoading] = useState(false);
  const auth = useSelector((state: any) => state.login);
  const { data, getService } = useGet();
  const { postService } = usePost();
  const { data: teleMedicationData, getService: getTeleMedicationData } =
    useGet();
  // const { data: frequencyResponse, getService: getFrequencyData } = useGet();
  const commonCellStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
  };

  const initialFormData = {
    startDate: "",
    medicineName: "",
    route: "",
    dose: "",
    initials: CommonFunctions.formatFirstAndLastName(
      auth?.userInfo?.userFirstName,
      auth?.userInfo?.userLastName,
      { suffixKey: "sn" }
    ),
    counterSignedBy: "",
    prescribedBy: "",
    isTeleOrder: true,
    caseId: caseId,
  };

  const [formData, setFormData] = React.useState({
    ...initialFormData,
    caseId: caseId,
  });

  const fields = [
    { label: "Medicine", key: "medicineName" },
    { label: "Route", key: "route" },
    { label: "Dose", key: "dose" },
    { label: "Check Initials (N1)", key: "initials" },
    {
      label: "Counter Signed By (RN)",
      key: "counterSignedBy",
    },
    {
      label: "Prescribed By",
      key: "prescribedBy",
    },
  ];

  const fetchTelephoneOrders = async () => {
    const teleMedicationData = await caseRepository.GetTelephoneOrders(
      caseId,
      setLoading
    );
    const sortedData = [...teleMedicationData].sort(
      (a: any, b: any) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
    setOriginalData(sortedData);
  };

  const openAdministerDialog = (params: any) => {
    setIsAdminOpen(true);
    setAdminRowData(params);
  };

  useEffect(() => {
    fetchTelephoneOrders();
  }, [caseId]);

  const handleButtonClick = () => {
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      formData.startDate = new Date().toISOString();
      formData.initials = formData.initials.trim();
      await postService(
        apiEndpoints.TELEMEDICATIONS,
        formData,
        `${formData.medicineName} Telephone Order has been Successfully Added.`
      );
      setOpen(false);
      await fetchTelephoneOrders();
      setFormData({
        ...initialFormData,
        caseId: caseId,
      });

      // setOpen(false);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setOpen(false);
  };

  const closeAdministerDialog = () => {
    setIsAdminOpen(false);
    fetchTelephoneOrders();
  };

  return (
    <div>
      <div className="headerDiv">
        <div className="iconHeading">
          <Heading type="h5" isBold={true} text="Prescription" />
          <IconButton
            className="iconBtn"
            onClick={handleButtonClick}
            text="Add New"
            icon={<AddCircleIcon fill="#fff" className=" addPlus-icon" />}
          />
        </div>
      </div>

      <React.Fragment>
        <GridTable
          rows={originalData}
          columns={columns}
          pageSizeOptions={[5, 10, 15]}
          initialPageSize={10}
          showSearchBar={false}
        />
        <TelephoneOrdersDialog
          open={open}
          onClose={handleClose}
          onSubmit={handleSubmit}
          title="Add Prescription"
          caption=" (All fields in this form are mandatory)"
          formData={formData}
          setFormData={setFormData}
          fields={fields}
          additionalContent={
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                fontWeight: 600,
              }}
            >
              <Heading type="body1" text="Date & Time:" font="16px" />
              <span
                style={{
                  color: "var(--color-secondary-contrastText)",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                &nbsp;
                <span>
                  {DateFormatter.formatTimeWithoutMeridiem(
                    new Date().toISOString()
                  )}
                </span>
              </span>
            </div>
          }
        />
        {isAdminOpen && (
          <AdministerDialog
            open={isAdminOpen}
            data={adminRowData}
            onClose={() => closeAdministerDialog()}
          />
        )}
      </React.Fragment>
    </div>
  );
}

export default TelephoneOrders;
